// =============================================================================
// VINUM - Cart Store
// Direct API calls — each action calls the backend immediately
// Optimistic local updates with server reconciliation
// =============================================================================

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { CartItem } from '../types';
import { CartAPI } from '../api';
import { STORAGE_KEYS } from '../constants';
import { calculateCartTotal, calculateCartItemCount } from '../utils';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface CartState {
  items: CartItem[];
  isLoading: boolean;
  isSyncing: boolean;
  error: string | null;
  lastSyncTime: number | null;
}

interface CartActions {
  // Cart operations
  addItem: (params: {
    userId: string;
    itemId: string;
    name: string;
    unitPrice: number;
    quantity?: number;
    imageUrl?: string;
  }) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  incrementItem: (itemId: string) => void;
  decrementItem: (itemId: string) => void;
  removeItem: (itemId: string) => void;
  clearCart: () => void;

  // Server sync
  loadCart: () => Promise<void>;

  // State management
  setLoading: (loading: boolean) => void;
  clearError: () => void;
}

type CartStore = CartState & CartActions;

// -----------------------------------------------------------------------------
// Store Implementation
// -----------------------------------------------------------------------------

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      // Initial state
      items: [],
      isLoading: false,
      isSyncing: false,
      error: null,
      lastSyncTime: null,

      // -----------------------------------------------------------------------
      // Add item to cart
      // Optimistic local update → API call → reconcile with server response
      // -----------------------------------------------------------------------
      addItem: ({ userId, itemId, name, unitPrice, quantity = 1, imageUrl }) => {
        // Optimistic update
        const currentItems = get().items;
        const existingIndex = currentItems.findIndex((item) => item.itemId === itemId);

        let optimisticItems: CartItem[];

        if (existingIndex >= 0) {
          optimisticItems = currentItems.map((item, index) => {
            if (index === existingIndex) {
              const newQty = item.quantity + quantity;
              return {
                ...item,
                quantity: newQty,
                totalPrice: item.unitPrice * newQty,
                synced: false,
              };
            }
            return item;
          });
        } else {
          const newItem: CartItem = {
            userId,
            itemId,
            name,
            unitPrice,
            quantity,
            totalPrice: unitPrice * quantity,
            imageUrl,
            synced: false,
            createdAt: new Date().toISOString(),
          };
          optimisticItems = [...currentItems, newItem];
        }

        set({ items: optimisticItems, error: null });

        // Fire API call (delta quantity — backend increments by this amount)
        CartAPI.addItem({ itemId, unitPrice, quantity })
          .then((serverItem) => {
            if (!serverItem || serverItem.quantity === 0) return;
            // Reconcile: replace the item with server truth
            const items = get().items.map((item) =>
              item.itemId === itemId
                ? { ...serverItem, synced: true }
                : item
            );
            set({ items, lastSyncTime: Date.now() });
          })
          .catch((error) => {
            console.error('Error adding item to cart:', error);
            // Rollback optimistic update
            set({ items: currentItems, error: 'Errore aggiunta al carrello' });
          });
      },

      // -----------------------------------------------------------------------
      // Set absolute quantity for an item
      // -----------------------------------------------------------------------
      updateQuantity: (itemId: string, quantity: number) => {
        if (quantity <= 0) {
          get().removeItem(itemId);
          return;
        }

        // Optimistic update
        const currentItems = get().items;
        const optimisticItems = currentItems.map((item) => {
          if (item.itemId === itemId) {
            return {
              ...item,
              quantity,
              totalPrice: item.unitPrice * quantity,
              synced: false,
            };
          }
          return item;
        });

        set({ items: optimisticItems, error: null });

        // API call (set absolute quantity)
        CartAPI.setQuantity(itemId, quantity)
          .then((serverItem) => {
            if (!serverItem) {
              // Item was removed (quantity set to 0)
              const items = get().items.filter((item) => item.itemId !== itemId);
              set({ items, lastSyncTime: Date.now() });
              return;
            }
            const items = get().items.map((item) =>
              item.itemId === itemId
                ? { ...serverItem, synced: true }
                : item
            );
            set({ items, lastSyncTime: Date.now() });
          })
          .catch((error) => {
            console.error('Error updating cart quantity:', error);
            set({ items: currentItems, error: 'Errore aggiornamento quantità' });
          });
      },

      incrementItem: (itemId: string) => {
        const item = get().items.find((i) => i.itemId === itemId);
        if (item) {
          get().updateQuantity(itemId, item.quantity + 1);
        }
      },

      decrementItem: (itemId: string) => {
        const item = get().items.find((i) => i.itemId === itemId);
        if (item) {
          if (item.quantity <= 1) {
            get().removeItem(itemId);
          } else {
            get().updateQuantity(itemId, item.quantity - 1);
          }
        }
      },

      // -----------------------------------------------------------------------
      // Remove item from cart
      // -----------------------------------------------------------------------
      removeItem: (itemId: string) => {
        const currentItems = get().items;
        const optimisticItems = currentItems.filter((item) => item.itemId !== itemId);
        set({ items: optimisticItems, error: null });

        CartAPI.removeItem(itemId).catch((error) => {
          console.error('Error removing cart item:', error);
          // Rollback
          set({ items: currentItems, error: 'Errore rimozione articolo' });
        });
      },

      // -----------------------------------------------------------------------
      // Clear entire cart
      // -----------------------------------------------------------------------
      clearCart: () => {
        const currentItems = get().items;
        set({ items: [], error: null });

        CartAPI.clearCart().catch((error) => {
          console.error('Error clearing cart on server:', error);
          set({ items: currentItems, error: 'Errore svuotamento carrello' });
        });
      },

      // -----------------------------------------------------------------------
      // Load cart from server (source of truth)
      // -----------------------------------------------------------------------
      loadCart: async () => {
        set({ isLoading: true, error: null });

        try {
          const cart = await CartAPI.getCart();
          set({
            items: cart.items,
            isLoading: false,
            lastSyncTime: Date.now(),
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Errore caricamento carrello';
          set({ isLoading: false, error: message });
        }
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: STORAGE_KEYS.CART_ITEMS,
      storage: createJSONStorage(() => {
        if (typeof window !== 'undefined' && window.localStorage) {
          return localStorage;
        }
        return {
          getItem: () => null,
          setItem: () => {},
          removeItem: () => {},
        };
      }),
      partialize: (state) => ({
        items: state.items,
        lastSyncTime: state.lastSyncTime,
      }),
    }
  )
);

// -----------------------------------------------------------------------------
// Selectors
// -----------------------------------------------------------------------------

export const selectCartItems = (state: CartStore) => state.items;
export const selectCartTotalItems = (state: CartStore) =>
  calculateCartItemCount(state.items);
export const selectCartTotalPrice = (state: CartStore) =>
  calculateCartTotal(state.items);
export const selectCartIsLoading = (state: CartStore) => state.isLoading;
export const selectCartIsSyncing = (state: CartStore) => state.isSyncing;
export const selectCartError = (state: CartStore) => state.error;

export const selectCartItemById = (itemId: string) => (state: CartStore) =>
  state.items.find((item) => item.itemId === itemId);

export const selectCartItemQuantity = (itemId: string) => (state: CartStore) => {
  const item = state.items.find((i) => i.itemId === itemId);
  return item?.quantity ?? 0;
};
